import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2, Star, Megaphone, Users, Truck, DoorOpen, Home, BarChart3 } from 'lucide-react';
import { Badge } from './ui/badge';
import api from '../services/api';

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' }); }
  catch { return ''; }
}

function StatPill({ icon: Icon, label, value, color = 'text-gk-teal' }) {
  return (
    <div data-testid={`pulse-stat-${label.toLowerCase().replace(/\s+/g, '-')}`} className="flex flex-col items-center justify-center p-3 bg-slate-50 rounded-lg border border-slate-200">
      <Icon className={`w-5 h-5 ${color} mb-1`} />
      <p className="text-2xl font-extrabold text-slate-900 leading-none">{value}</p>
      <p className="text-[11px] text-slate-500 mt-1 text-center">{label}</p>
    </div>
  );
}

function DailyChart({ data }) {
  const max = Math.max(1, ...data.map(d => d.count));
  return (
    <div data-testid="pulse-daily-chart" className="space-y-1">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500 w-16 shrink-0">{d.date}</span>
          <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
            <div className="bg-gradient-to-r from-gk-teal to-cyan-500 h-3 rounded-full" style={{ width: `${(d.count / max) * 100}%` }} />
          </div>
          <span className="text-[11px] font-semibold text-slate-700 w-6 text-right">{d.count}</span>
        </div>
      ))}
    </div>
  );
}

export function ThisWeekModal({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/community/this-week')
      .then(res => setData(res.data))
      .catch(e => console.error('this-week fetch failed', e))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="this-week-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="w-5 h-5 text-cyan-600" />
            This Week
          </DialogTitle>
          <DialogDescription>
            {data ? `${data.community_name} · ${formatDate(data.week_start)} → ${formatDate(data.week_end)}` : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
        ) : (
          <div className="space-y-5 py-2">
            {data.personal && (
              <section>
                <h3 className="text-sm font-bold text-slate-800 mb-2">Your week</h3>
                <div className="grid grid-cols-3 gap-2">
                  <StatPill icon={DoorOpen} label="Guests entered" value={data.personal.passes_used} color="text-emerald-500" />
                  <StatPill icon={Truck} label="Deliveries received" value={data.personal.deliveries} color="text-amber-500" />
                  <StatPill icon={Home} label="Visitors at your villa" value={data.personal.visitors_at_villa} color="text-indigo-500" />
                </div>
                {data.personal.recent_my_passes?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Your gate passes this week</p>
                    {data.personal.recent_my_passes.map(p => (
                      <div key={p.id} data-testid={`pulse-pass-${p.id}`} className="flex items-center justify-between p-2 bg-slate-50 rounded text-xs">
                        <div className="min-w-0 flex-1">
                          <span className="font-semibold text-slate-800 truncate">{p.visitor_name || 'Visitor'}</span>
                          {p.pass_category && <Badge variant="outline" className="ml-2 text-[9px] capitalize">{p.pass_category}</Badge>}
                        </div>
                        <Badge className={`text-[9px] ${p.status === 'used' ? 'bg-emerald-500' : 'bg-amber-500'}`}>{p.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            <section>
              <h3 className="text-sm font-bold text-slate-800 mb-2">Community at a glance</h3>
              <div className="grid grid-cols-3 gap-2">
                <StatPill icon={Users} label="Visitors" value={data.community.visitors_total} color="text-blue-500" />
                <StatPill icon={DoorOpen} label="ANPR scans" value={data.community.anpr_total} color="text-cyan-500" />
                <StatPill icon={Users} label="New residents" value={data.community.new_residents_count} color="text-purple-500" />
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold text-slate-800 mb-2">Daily visitor traffic</h3>
              <DailyChart data={data.community.daily_traffic} />
            </section>

            <section>
              <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5">
                <Star className="w-4 h-4 text-amber-500" /> Top-rated recommendations
              </h3>
              {data.community.top_recs.length === 0 ? (
                <p className="text-xs text-slate-500 italic">No rated recommendations yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.community.top_recs.map(r => (
                    <div key={r.id} data-testid={`pulse-rec-${r.id}`} className="flex items-center justify-between p-2.5 bg-amber-50 border border-amber-100 rounded-lg">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{r.business_name}</p>
                        <p className="text-[11px] text-slate-500 capitalize">{r.category}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-amber-600">★ {r.rating_avg.toFixed(1)}</p>
                        <p className="text-[10px] text-slate-500">{r.rating_count} rating{r.rating_count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {data.community.new_residents.length > 0 && (
              <section>
                <h3 className="text-sm font-bold text-slate-800 mb-2">New neighbors this week</h3>
                <div className="flex flex-wrap gap-1.5">
                  {data.community.new_residents.map(n => (
                    <Badge key={n.id} variant="outline" className="text-[11px]">{n.name} · {n.villa_number}</Badge>
                  ))}
                </div>
              </section>
            )}

            {data.community.recent_notices.length > 0 && (
              <section>
                <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5">
                  <Megaphone className="w-4 h-4 text-blue-500" /> Recent notices
                </h3>
                <div className="space-y-1">
                  {data.community.recent_notices.map(n => (
                    <div key={n.id} data-testid={`pulse-notice-${n.id}`} className="flex items-center justify-between p-2 bg-blue-50 rounded text-xs">
                      <span className="font-semibold text-slate-800 truncate">{n.title}</span>
                      <span className="text-slate-500 text-[10px] shrink-0 ml-2">{formatDate(n.created_at)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
